import { z } from "zod";

export const VOICE_GUESSER_STATUS = {
	NONE: "none",
	LOBBY: "lobby",
	RECORDING: "recording",
	IN_ROUND: "in_round",
	BETWEEN_ROUNDS: "between_rounds",
	ENDED: "ended",
	CANCELED: "canceled",
} as const;

export type VoiceGuesserStatus = (typeof VOICE_GUESSER_STATUS)[keyof typeof VOICE_GUESSER_STATUS];

export const voiceGuesserStatusSchema = z.enum([
	VOICE_GUESSER_STATUS.NONE,
	VOICE_GUESSER_STATUS.LOBBY,
	VOICE_GUESSER_STATUS.RECORDING,
	VOICE_GUESSER_STATUS.IN_ROUND,
	VOICE_GUESSER_STATUS.BETWEEN_ROUNDS,
	VOICE_GUESSER_STATUS.ENDED,
	VOICE_GUESSER_STATUS.CANCELED,
]);

export const VOICE_GUESSER_CANCEL_REASONS = {
	HOST_LEFT: "host_left",
	LOBBY_EXPIRED: "lobby_expired",
	NOT_ENOUGH_RECORDINGS: "not_enough_recordings",
	LAST_PLAYER_REMAINING: "last_player_remaining",
	MANUAL: "manual",
} as const;

export type VoiceGuesserCancelReason = (typeof VOICE_GUESSER_CANCEL_REASONS)[keyof typeof VOICE_GUESSER_CANCEL_REASONS];

export const voiceGuesserCancelReasonSchema = z.enum([
	VOICE_GUESSER_CANCEL_REASONS.HOST_LEFT,
	VOICE_GUESSER_CANCEL_REASONS.LOBBY_EXPIRED,
	VOICE_GUESSER_CANCEL_REASONS.NOT_ENOUGH_RECORDINGS,
	VOICE_GUESSER_CANCEL_REASONS.LAST_PLAYER_REMAINING,
	VOICE_GUESSER_CANCEL_REASONS.MANUAL,
]);

export const VOICE_GUESSER_RECORDING_DURATION_MS = 6_000;
export const VOICE_GUESSER_RECORDING_PREP_MS = 7_000;
export const VOICE_GUESSER_RECORDING_UPLOAD_GRACE_MS = 4_000;
export const VOICE_GUESSER_RECORDINGS_PER_PLAYER_OPTIONS = [1, 3, 5] as const;
export type VoiceGuesserRecordingsPerPlayer = (typeof VOICE_GUESSER_RECORDINGS_PER_PLAYER_OPTIONS)[number];
export const voiceGuesserRecordingsPerPlayerSchema = z.union([z.literal(1), z.literal(3), z.literal(5)]);
export const VOICE_GUESSER_RECORDINGS_PER_PLAYER: VoiceGuesserRecordingsPerPlayer = 3;

export const VOICE_GUESSER_REACTION_EMOJIS = ["😂", "🤬", "👍", "🥲", "🎉"] as const;
export type VoiceGuesserReactionEmoji = (typeof VOICE_GUESSER_REACTION_EMOJIS)[number];
export const voiceGuesserReactionEmojiSchema = z.enum(VOICE_GUESSER_REACTION_EMOJIS);
export const VOICE_GUESSER_BETWEEN_RECORDINGS_MS = 1_500;
export const VOICE_GUESSER_ANSWER_WINDOW_MS = 12_000;
export const VOICE_GUESSER_ROUND_DURATION_MS = 12_000;
export const VOICE_GUESSER_LOBBY_TTL_MS = 5 * 60 * 1000;
export const VOICE_GUESSER_LOBBY_AUTO_START_MS = 60 * 1000;
export const VOICE_GUESSER_BETWEEN_ROUNDS_MS = 3_000;
export const VOICE_GUESSER_MIN_PLAYERS = 2;
export const VOICE_GUESSER_MAX_PLAYERS = 10;
export const VOICE_GUESSER_MIN_GROUP_MEMBERS = 2;
export const VOICE_GUESSER_BASE_POINTS = 10;
export const VOICE_GUESSER_MAX_SPEED_BONUS = 5;
export const VOICE_GUESSER_MAX_OPTIONS_PER_ROUND = 4;
export const VOICE_GUESSER_MIN_OPTIONS_PER_ROUND = 2;

export const VOICE_GUESSER_EFFECTS = {
	PITCH_UP: "pitch_up",
	PITCH_DOWN: "pitch_down",
	MONSTER: "monster",
	ROBOT: "robot",
	ALIEN: "alien",
	ECHO: "echo",
} as const;

export const VOICE_GUESSER_EFFECT_VALUES = [
	VOICE_GUESSER_EFFECTS.PITCH_UP,
	VOICE_GUESSER_EFFECTS.PITCH_DOWN,
	VOICE_GUESSER_EFFECTS.MONSTER,
	VOICE_GUESSER_EFFECTS.ROBOT,
	VOICE_GUESSER_EFFECTS.ALIEN,
	VOICE_GUESSER_EFFECTS.ECHO,
] as const;

export const voiceGuesserEffectSchema = z.enum(VOICE_GUESSER_EFFECT_VALUES);
export type VoiceGuesserEffect = z.infer<typeof voiceGuesserEffectSchema>;

export const VOICE_GUESSER_EFFECT_PLAYBACK_RATE: Record<VoiceGuesserEffect, number> = {
	[VOICE_GUESSER_EFFECTS.PITCH_UP]: 1.42,
	[VOICE_GUESSER_EFFECTS.PITCH_DOWN]: 0.68,
	[VOICE_GUESSER_EFFECTS.MONSTER]: 0.6,
	[VOICE_GUESSER_EFFECTS.ROBOT]: 1.18,
	[VOICE_GUESSER_EFFECTS.ALIEN]: 1.72,
	[VOICE_GUESSER_EFFECTS.ECHO]: 0.82,
};
